import { useCallback, useEffect, useState } from 'react';
import { type Theme, type ThemeChoice, nextChoice, readChoice, resolveTheme, writeChoice } from '@seemantham/core';

const QUERY = '(prefers-color-scheme: dark)';

function storage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

/** The device decides unless someone chose otherwise; the choice sticks. */
export function useTheme(): { theme: Theme; choice: ThemeChoice; toggle: () => void } {
  const [choice, setChoice] = useState<ThemeChoice>(() => readChoice(storage()));
  const [prefersDark, setPrefersDark] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.(QUERY).matches,
  );

  useEffect(() => {
    const media = window.matchMedia?.(QUERY);
    if (!media) return;
    const onChange = (event: MediaQueryListEvent) => setPrefersDark(event.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const theme = resolveTheme({ stored: choice === 'system' ? null : choice, prefersDark });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  const toggle = useCallback(() => {
    setChoice((current) => {
      const next = nextChoice(current, resolveTheme({ stored: current === 'system' ? null : current, prefersDark }));
      writeChoice(next, storage());
      return next;
    });
  }, [prefersDark]);

  return { theme, choice, toggle };
}
